/**
 * User Profile Banner Component
 * Displays authenticated user's full name and email in a banner at the top of the dashboard
 */

import React, { useEffect, useState } from 'react';
import styles from './ProfileBanner.module.css';

interface UserProfileData {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  statusCode: string;
}

interface ProfileBannerProps {
  initialUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

const ProfileBanner: React.FC<ProfileBannerProps> = ({ initialUser }) => {
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user profile from Dynamics CRM via backend API
        const response = await fetch('/api/Home/UserProfile', {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('[ProfileBanner] API response:', { status: response.status, ok: response.ok });

        if (!response.ok) {
          // If API fails, use initialUser data as fallback
          if (initialUser) {
            setUserProfile({
              firstName: initialUser.firstName || '',
              lastName: initialUser.lastName || '',
              fullName: `${initialUser.firstName || ''} ${initialUser.lastName || ''}`.trim(),
              email: initialUser.email || '',
              statusCode: '0'
            });
            setError(null);
          } else {
            throw new Error(`API returned ${response.status}`);
          }
        } else {
          const data: UserProfileData = await response.json();
          console.log('[ProfileBanner] User profile received:', data);
          setUserProfile(data);
        }
      } catch (err) {
        console.error('[ProfileBanner] Error fetching user profile:', err);
        
        // Fallback to initialUser data
        if (initialUser) {
          setUserProfile({
            firstName: initialUser.firstName || '',
            lastName: initialUser.lastName || '',
            fullName: `${initialUser.firstName || ''} ${initialUser.lastName || ''}`.trim(),
            email: initialUser.email || '',
            statusCode: '0'
          });
          setError(null);
        } else {
          setError('Unable to load profile information');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [initialUser]);

  if (loading) {
    return (
      <div className={styles.banner}>
        <div className={styles.content}>
          <div className={styles.skeleton} />
        </div>
      </div>
    );
  }

  if (error && !userProfile) {
    return (
      <div className={styles.banner}>
        <div className={styles.content}>
          <p className={styles.error}>{error}</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <div className={styles.content}>
        <div className={styles.profileInfo}>
          <div className={styles.avatar}>
            {userProfile.fullName.charAt(0).toUpperCase()}
          </div>
          <div className={styles.details}>
            <h2 className={styles.name}>{userProfile.fullName}</h2>
            <p className={styles.email}>{userProfile.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileBanner;
